import {
  isConnectorEnvironment,
  isConnectorId,
  type ConnectorEnvironment,
  type ConnectorId,
} from "./settings-connectors";

export const connectorRotationReasons = [
  "SCHEDULED_ROTATION",
  "SUSPECTED_COMPROMISE",
  "ACCESS_CHANGE",
  "PROVIDER_REQUIREMENT",
  "RECOVERY",
] as const;

export type ConnectorRotationReason = (typeof connectorRotationReasons)[number];

export interface ConnectorMetadataPatchRequest {
  readonly expectedVersion: number;
  readonly provider: string;
  readonly mode: string;
  readonly safeIdentifierAlias: string | null;
}

export type ConnectorMetadataPatchOutcome =
  | {
      readonly kind: "confirmed";
      readonly connectorId: ConnectorId;
      readonly environment: ConnectorEnvironment;
      readonly previousVersion: number | null;
      readonly configurationVersion: number;
      readonly provider: string;
      readonly mode: string;
      readonly safeIdentifierAlias: string | null;
      readonly replayed: boolean;
    }
  | {
      readonly kind: "conflict";
      readonly code: "STALE_VERSION" | "IDEMPOTENCY_CONFLICT";
    };

export interface ConnectorRotationRequest {
  readonly secretName: string;
  readonly reason: ConnectorRotationReason;
  readonly requestedVersion: number;
}

export type ConnectorRotationRequestOutcome =
  | {
      readonly kind: "confirmed";
      readonly requestId: string;
      readonly connectorId: ConnectorId;
      readonly environment: ConnectorEnvironment;
      readonly secretName: string;
      readonly reason: ConnectorRotationReason;
      readonly requestedVersion: number;
      readonly status: "REQUESTED";
      readonly expiresAt: string;
      readonly requestedAt: string;
      readonly replayed: boolean;
    }
  | {
      readonly kind: "conflict";
      readonly code: "STALE_VERSION" | "IDEMPOTENCY_CONFLICT";
    };

const rotationReasonSet: ReadonlySet<string> = new Set(
  connectorRotationReasons,
);
const providerModePattern = /^[a-z][a-z0-9._+-]{0,127}$/;
const safeIdentifierPattern = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const secretNamePattern = /^[A-Z][A-Z0-9_]{2,127}$/;
const isoInstantPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => key in value);
}

function isNullableSafeIdentifier(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === "string" && safeIdentifierPattern.test(value))
  );
}

function isIsoInstant(value: unknown): value is string {
  return (
    typeof value === "string" &&
    isoInstantPattern.test(value) &&
    new Date(value).toISOString() === value
  );
}

function isVersion(value: unknown, allowZero: boolean): value is number {
  return (
    Number.isSafeInteger(value) && (value as number) >= (allowZero ? 0 : 1)
  );
}

export function isConnectorMetadataPatchRequest(
  value: unknown,
): value is ConnectorMetadataPatchRequest {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, [
      "expectedVersion",
      "provider",
      "mode",
      "safeIdentifierAlias",
    ]) &&
    isVersion(value.expectedVersion, true) &&
    typeof value.provider === "string" &&
    providerModePattern.test(value.provider) &&
    typeof value.mode === "string" &&
    providerModePattern.test(value.mode) &&
    isNullableSafeIdentifier(value.safeIdentifierAlias)
  );
}

export function isConnectorRotationRequest(
  value: unknown,
): value is ConnectorRotationRequest {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, ["secretName", "reason", "requestedVersion"]) &&
    typeof value.secretName === "string" &&
    secretNamePattern.test(value.secretName) &&
    typeof value.reason === "string" &&
    rotationReasonSet.has(value.reason) &&
    isVersion(value.requestedVersion, false)
  );
}

export function isConnectorMetadataPatchOutcome(
  value: unknown,
): value is ConnectorMetadataPatchOutcome {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  if (value.kind === "conflict") {
    return (
      hasExactKeys(value, ["kind", "code"]) &&
      (value.code === "STALE_VERSION" || value.code === "IDEMPOTENCY_CONFLICT")
    );
  }
  if (value.kind !== "confirmed") return false;
  return (
    hasExactKeys(value, [
      "kind",
      "connectorId",
      "environment",
      "previousVersion",
      "configurationVersion",
      "provider",
      "mode",
      "safeIdentifierAlias",
      "replayed",
    ]) &&
    isConnectorId(value.connectorId) &&
    isConnectorEnvironment(value.environment) &&
    (value.previousVersion === null ||
      isVersion(value.previousVersion, false)) &&
    isVersion(value.configurationVersion, false) &&
    value.configurationVersion === (value.previousVersion ?? 0) + 1 &&
    typeof value.provider === "string" &&
    providerModePattern.test(value.provider) &&
    typeof value.mode === "string" &&
    providerModePattern.test(value.mode) &&
    isNullableSafeIdentifier(value.safeIdentifierAlias) &&
    typeof value.replayed === "boolean"
  );
}

export function isConnectorRotationRequestOutcome(
  value: unknown,
): value is ConnectorRotationRequestOutcome {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  if (value.kind === "conflict") {
    return (
      hasExactKeys(value, ["kind", "code"]) &&
      (value.code === "STALE_VERSION" || value.code === "IDEMPOTENCY_CONFLICT")
    );
  }
  if (value.kind !== "confirmed") return false;
  return (
    hasExactKeys(value, [
      "kind",
      "requestId",
      "connectorId",
      "environment",
      "secretName",
      "reason",
      "requestedVersion",
      "status",
      "expiresAt",
      "requestedAt",
      "replayed",
    ]) &&
    typeof value.requestId === "string" &&
    safeIdentifierPattern.test(value.requestId) &&
    isConnectorId(value.connectorId) &&
    isConnectorEnvironment(value.environment) &&
    typeof value.secretName === "string" &&
    secretNamePattern.test(value.secretName) &&
    typeof value.reason === "string" &&
    rotationReasonSet.has(value.reason) &&
    isVersion(value.requestedVersion, false) &&
    value.status === "REQUESTED" &&
    isIsoInstant(value.requestedAt) &&
    isIsoInstant(value.expiresAt) &&
    value.expiresAt > value.requestedAt &&
    typeof value.replayed === "boolean"
  );
}
